import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { gbp } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { captureReferralSource } from "../lib/referral";
import { PublicFooter, PublicHeader, PublicSignupCTA } from "../components/PublicPageChrome";

// Starting estimates only. Every price is editable. Deliberately not stamped with a
// retailer/date, since we can't stand behind specific current prices we haven't verified.
const PRESETS = [
  {
    key: "cupcakes",
    label: "12 vanilla cupcakes",
    portion_count: 12,
    labour_minutes: 45,
    packaging_cost: 0.35,
    lines: [
      { name: "Butter", cost: 2.1 },
      { name: "Sugar", cost: 1.4 },
      { name: "Flour", cost: 0.9 },
      { name: "Eggs", cost: 1.8 },
      { name: "Vanilla & misc", cost: 0.6 },
      { name: "Buttercream & decoration", cost: 3.2 },
    ],
  },
  {
    key: "cake",
    label: "8\" two-tier celebration cake",
    portion_count: 20,
    labour_minutes: 180,
    packaging_cost: 0.15,
    lines: [
      { name: "Sponge ingredients", cost: 9.5 },
      { name: "Buttercream & filling", cost: 6.8 },
      { name: "Fondant & decoration", cost: 8.0 },
      { name: "Board & dowels", cost: 2.2 },
    ],
  },
  {
    key: "sourdough",
    label: "Dozen sourdough loaves",
    portion_count: 12,
    labour_minutes: 240,
    packaging_cost: 0.3,
    lines: [
      { name: "Strong bread flour (6kg)", cost: 7.2 },
      { name: "Salt", cost: 0.2 },
      { name: "Starter & misc", cost: 0.3 },
    ],
  },
  {
    key: "platter",
    label: "Grazing platter for 30",
    portion_count: 30,
    labour_minutes: 90,
    packaging_cost: 0.4,
    lines: [
      { name: "Cured meats", cost: 18 },
      { name: "Cheeses", cost: 15 },
      { name: "Crackers & bread", cost: 6 },
      { name: "Fruit, nuts & olives", cost: 9 },
      { name: "Dips", cost: 5 },
    ],
  },
  {
    key: "curry",
    label: "Butter chicken & rice, 80 guests",
    portion_count: 80,
    labour_minutes: 300,
    packaging_cost: 0.5,
    lines: [
      { name: "Chicken", cost: 120 },
      { name: "Tomato & cream base", cost: 45 },
      { name: "Spices", cost: 12 },
      { name: "Rice", cost: 18 },
      { name: "Naan & garnish", cost: 25 },
    ],
  },
];

const inputCls = "h-10 px-2 rounded-md border border-sand bg-white text-sm focus:outline-none focus:ring-2 focus:ring-tea-hover/70 w-full";
// Reserves 2 lines of height so a label that wraps ("Packaging £/portion") doesn't push its
// input lower than shorter labels ("Portions") sharing the same grid row.
const fieldLabelCls = "block min-h-8 text-xs uppercase tracking-[0.2em] font-medium text-ink/50";

function loadPreset(preset) {
  return {
    name: preset.label,
    portion_count: preset.portion_count,
    labour_minutes: preset.labour_minutes,
    labour_rate: 15,
    packaging_cost: preset.packaging_cost,
    selling_price: "",
    target_food_cost_pct: 30,
    batches_per_month: 8,
    lines: preset.lines.map((l) => ({ ...l, _key: crypto.randomUUID() })),
  };
}

export default function PriceFloorCalculator({ embedded = false }) {
  const [form, setFormState] = useState(() => loadPreset(PRESETS[0]));
  const [activePreset, setActivePreset] = useState(PRESETS[0].key);
  const calculatorUsed = useRef(false);

  const setForm = (value) => {
    if (!calculatorUsed.current) {
      calculatorUsed.current = true;
      trackEvent("calculator-used");
    }
    setFormState(value);
  };

  useEffect(() => { captureReferralSource(); }, []);

  const applyPreset = (preset) => {
    setActivePreset(preset.key);
    setForm(loadPreset(preset));
  };

  const setLine = (i, key, value) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [key]: value };
    setForm({ ...form, lines });
  };

  const costing = useMemo(() => {
    const ingredientTotal = form.lines.reduce((sum, l) => sum + (parseFloat(l.cost) || 0), 0);
    const portions = Math.max(parseInt(form.portion_count) || 1, 1);
    const ingredientPP = ingredientTotal / portions;
    const labourPP = ((parseFloat(form.labour_minutes) || 0) / 60) * (parseFloat(form.labour_rate) || 0) / portions;
    const packagingPP = parseFloat(form.packaging_cost) || 0;
    const totalPP = ingredientPP + labourPP + packagingPP;
    const sellingPP = parseFloat(form.selling_price) || 0;
    const foodCostPct = sellingPP > 0 ? (totalPP / sellingPP) * 100 : null;
    const marginPct = sellingPP > 0 ? ((sellingPP - totalPP) / sellingPP) * 100 : null;
    const targetPct = Math.min(Math.max(parseFloat(form.target_food_cost_pct) || 30, 1), 99);
    const priceFloor = totalPP / (targetPct / 100);
    const gapPerBatch = sellingPP > 0 ? (priceFloor - sellingPP) * portions : null;
    const batchesPerYear = (parseFloat(form.batches_per_month) || 0) * 12;
    const annualGap = gapPerBatch != null ? gapPerBatch * batchesPerYear : null;
    return { ingredientTotal, ingredientPP, labourPP, packagingPP, totalPP, sellingPP, foodCostPct, marginPct, priceFloor, gapPerBatch, annualGap };
  }, [form]);

  const hasResult = costing.sellingPP > 0;
  const underpriced = hasResult && costing.gapPerBatch > 0.01;

  const Heading = embedded ? "h2" : "h1";
  const body = (
    <>
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-tea-text">Free price-floor check &middot; no sign-up</p>
          <Heading className={`font-heading font-bold tracking-tight mt-2 [text-wrap:balance] ${embedded ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl"}`}>
            What should you actually charge for this?
          </Heading>
          <p className="mt-4 text-ink/60 text-base sm:text-lg [text-wrap:pretty]">
            Pick a starting point, adjust the numbers to match your own business, and see your real cost and price floor,
            instantly, on screen, before we ask you for anything.
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50 mb-2">Start from a preset</p>
          <div className="flex flex-wrap gap-2" data-testid="calculator-presets">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`calculator-preset-${p.key}`}
                onClick={() => applyPreset(p)}
                className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors duration-150 ${
                  activePreset === p.key ? "bg-ink text-white border-ink" : "bg-white border-sand text-ink/70 hover:border-ink/30"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Dish name</label>
              <input data-testid="calculator-name-input" className={`${inputCls} mt-1 h-12`} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className={fieldLabelCls}>Portions</label>
                <input type="number" min="1" data-testid="calculator-portions-input" className={`${inputCls} mt-1 font-mono-num`} value={form.portion_count}
                  onChange={(e) => setForm({ ...form, portion_count: e.target.value })} />
              </div>
              <div>
                <label className={fieldLabelCls}>Your time (min)</label>
                <input type="number" min="0" data-testid="calculator-labour-input" className={`${inputCls} mt-1 font-mono-num`} value={form.labour_minutes}
                  onChange={(e) => setForm({ ...form, labour_minutes: e.target.value })} />
                <a href="/learn/labour-costing" className="mt-1 inline-block text-[11px] text-tea-text hover:underline">
                  What should this include? →
                </a>
              </div>
              <div>
                <label className={fieldLabelCls}>Your rate £/hr</label>
                <input type="number" step="0.5" min="0" data-testid="calculator-labour-rate-input" className={`${inputCls} mt-1 font-mono-num`} value={form.labour_rate}
                  onChange={(e) => setForm({ ...form, labour_rate: e.target.value })} />
              </div>
              <div>
                <label className={fieldLabelCls}>Packaging £/portion</label>
                <input type="number" step="0.01" min="0" data-testid="calculator-packaging-input" className={`${inputCls} mt-1 font-mono-num`} value={form.packaging_cost}
                  onChange={(e) => setForm({ ...form, packaging_cost: e.target.value })} />
              </div>
            </div>

            <div>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Ingredients (cost for the whole batch)</label>
                  <a href="/learn/yield" className="mt-1 block text-[11px] text-tea-text hover:underline">
                    Why your real cost isn't the receipt price →
                  </a>
                </div>
                <button data-testid="calculator-add-line-button"
                  onClick={() => setForm({ ...form, lines: [...form.lines, { _key: crypto.randomUUID(), name: "", cost: 0 }] })}
                  className="flex shrink-0 items-center gap-1 text-sm text-tea-text font-medium hover:underline"><Plus size={15} /> Add ingredient</button>
              </div>
              <div className="space-y-2">
                {form.lines.map((line, i) => (
                  <div key={line._key} className="grid grid-cols-12 gap-2 items-center" data-testid={`calculator-line-${i}`}>
                    <input placeholder="Ingredient" data-testid={`calculator-line-name-${i}`} className={`${inputCls} col-span-7`} value={line.name}
                      onChange={(e) => setLine(i, "name", e.target.value)} />
                    <div className="col-span-4 relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-ink/40 text-sm">£</span>
                      <input type="number" step="0.01" min="0" data-testid={`calculator-line-cost-${i}`} className={`${inputCls} pl-5 font-mono-num`} value={line.cost}
                        onChange={(e) => setLine(i, "cost", e.target.value)} />
                    </div>
                    <button data-testid={`calculator-line-delete-${i}`} onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}
                      className="col-span-1 p-2 text-ink/40 hover:text-tea-text"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-ink/40 mt-2">Enter what you actually paid for the batch. Every line is editable, and nothing here is guessed on your behalf.</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className={fieldLabelCls}>What you charge £/portion</label>
                <input type="number" step="0.01" min="0" placeholder="e.g. 2.50" data-testid="calculator-selling-price-input" className={`${inputCls} mt-1 font-mono-num`} value={form.selling_price}
                  onChange={(e) => setForm({ ...form, selling_price: e.target.value })} />
              </div>
              <div>
                <label className={fieldLabelCls}>Target food cost %</label>
                <input type="number" step="1" min="1" max="99" data-testid="calculator-target-pct-input" className={`${inputCls} mt-1 font-mono-num`} value={form.target_food_cost_pct}
                  onChange={(e) => setForm({ ...form, target_food_cost_pct: e.target.value })} />
              </div>
              <div>
                <label className={fieldLabelCls}>Batches sold / month</label>
                <input type="number" step="1" min="0" data-testid="calculator-batches-input" className={`${inputCls} mt-1 font-mono-num`} value={form.batches_per_month}
                  onChange={(e) => setForm({ ...form, batches_per_month: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-6 h-fit bg-ink text-white rounded-lg p-6 space-y-4" data-testid="calculator-cost-panel">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-white/60">True cost per portion</p>
            <p className="font-mono-num text-4xl font-semibold text-tea" data-testid="calculator-cost-per-portion">{gbp(costing.totalPP)}</p>
            <div className="space-y-2 text-sm border-t border-white/20 pt-4">
              <div className="flex justify-between"><span className="text-white/70">Ingredients</span><span className="font-mono-num">{gbp(costing.ingredientPP)}</span></div>
              <div className="flex justify-between"><span className="text-white/70">Your time</span><span className="font-mono-num">{gbp(costing.labourPP)}</span></div>
              <div className="flex justify-between"><span className="text-white/70">Packaging</span><span className="font-mono-num">{gbp(costing.packagingPP)}</span></div>
            </div>

            <div className="border-t border-white/20 pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/70">Price floor at {form.target_food_cost_pct || 30}% food cost</span>
                <span className="font-mono-num text-tea font-semibold" data-testid="calculator-price-floor">{gbp(costing.priceFloor)}</span>
              </div>
              {hasResult && costing.marginPct != null && (
                <div className="flex justify-between" data-testid="calculator-margin-pct">
                  <span className="text-white/70">Your current margin</span>
                  <span className="font-mono-num">{costing.marginPct.toFixed(1)}%</span>
                </div>
              )}
            </div>

            {hasResult && (
              <div className={`rounded-lg p-4 text-sm ${underpriced ? "bg-almond/20" : "bg-tea/20"}`} data-testid="calculator-headline">
                {underpriced ? (
                  <>
                    <p className="font-semibold text-white">You're leaving {gbp(costing.gapPerBatch)} on the table on this batch.</p>
                    {costing.annualGap > 0 && (
                      <p className="text-white/70 mt-1">That's about {gbp(costing.annualGap)}/year at {form.batches_per_month || 0} batches a month.</p>
                    )}
                  </>
                ) : (
                  <p className="font-semibold text-white">You're pricing this at or above your floor. Nice work.</p>
                )}
              </div>
            )}

            {!hasResult && (
              <p className="text-white/40 text-xs pt-2">Enter what you currently charge per portion to see your margin and gap.</p>
            )}
          </div>
        </div>

        <PublicSignupCTA
          eyebrow="This was one recipe"
          heading="Cost your whole menu in an evening."
          body="Save this recipe and add the rest. Snap your receipts, get a price floor for every quote, then send the invoice and get paid by card, Klarna or Clearpay."
          testId="calculator-signup-cta"
        />
    </>
  );

  if (embedded) {
    return (
      <div className="max-w-5xl mx-auto px-5 pb-16 space-y-8" data-testid="price-floor-calculator">
        {body}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream text-ink font-body" data-testid="price-floor-calculator">
      <PublicHeader />
      <main className="max-w-5xl mx-auto px-5 pb-24 space-y-8 animate-rise">
        {body}
      </main>
      <PublicFooter />
    </div>
  );
}
